import { toast } from "react-hot-toast";
import { apiConnector } from "../apiConnector";
import { subjectEndpoints } from "../apis";

const { CREATE_CLASS_API, CLASS_BASE_API, GET_ALL_CLASSES_API } = subjectEndpoints;

const getAuthHeader = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  return { Authorization: `Bearer ${token}` };
};

// =========================================================
// NEW: GET ALL CLASSES
// =========================================================
export async function getAllClasses() {
  try {
    const response = await apiConnector(
      "GET",
      GET_ALL_CLASSES_API || CLASS_BASE_API, // Use whichever you defined
      null,
      getAuthHeader()
    );
    return response.data;
  } catch (error) {
    toast.error("Failed to fetch classes");
    return [];
  }
}

// =========================================================
// 1. CREATE A NEW CLASS
// =========================================================
export async function createClass(classData) {
  const toastId = toast.loading("Creating class...");
  try {
    const response = await apiConnector(
      "POST",
      CREATE_CLASS_API,
      classData, // e.g., { _id: "cbse-06", class_name: "6", subjects: {...} }
      getAuthHeader()
    );

    toast.success("Class created successfully!");
    return response.data;
  } catch (error) {
    toast.error(error.response?.data?.message || "Failed to create class");
    return null;
  } finally {
    toast.dismiss(toastId);
  }
}

// =========================================================
// 2. GET CLASS DETAILS (Read)
// =========================================================
export async function getClassDetails(classId) {
  try {
    const response = await apiConnector(
      "GET",
      `${CLASS_BASE_API}/${classId}`,
      null,
      getAuthHeader()
    );
    return response.data;
  } catch (error) {
    toast.error("Failed to fetch class details");
    return null;
  }
}

// =========================================================
// 3. ADD OR UPDATE ENTIRE SUBJECT
// =========================================================
export async function addOrUpdateSubject(classId, subjectName, chapters) {
  const toastId = toast.loading(`Updating ${subjectName}...`);
  try {
    const response = await apiConnector(
      "PUT",
      `${CLASS_BASE_API}/${classId}/subjects`,
      { subjectName, chapters }, // Body
      getAuthHeader()
    );

    toast.success(`${subjectName} updated successfully!`);
    return response.data;
  } catch (error) {
    toast.error(`Failed to update ${subjectName}`);
    return null;
  } finally {
    toast.dismiss(toastId);
  }
}

// =========================================================
// 4. ADD NEW CHAPTER(S) TO SUBJECT
// =========================================================
export async function addChaptersToSubject(classId, subjectName, newChapters) {
  const toastId = toast.loading("Adding chapters...");
  try {
    const response = await apiConnector(
      "PUT",
      // encodeURIComponent handles subjects with spaces like "social science"
      `${CLASS_BASE_API}/${classId}/subjects/${encodeURIComponent(subjectName)}/chapters`,
      { newChapters }, // Body: array of strings
      getAuthHeader()
    );

    toast.success("Chapters added successfully!");
    return response.data;
  } catch (error) {
    toast.error("Failed to add chapters");
    return null;
  } finally {
    toast.dismiss(toastId);
  }
}

// =========================================================
// 5. REMOVE A SPECIFIC CHAPTER
// =========================================================
export async function removeChapterFromSubject(classId, subjectName, chapterToRemove) {
  const toastId = toast.loading("Removing chapter...");
  try {
    const response = await apiConnector(
      "DELETE",
      `${CLASS_BASE_API}/${classId}/subjects/${encodeURIComponent(subjectName)}/chapters/remove`,
      { chapterToRemove }, // Body: { chapterToRemove: "Chapter Name" }
      getAuthHeader()
    );

    toast.success("Chapter removed successfully!");
    return response.data;
  } catch (error) {
    toast.error("Failed to remove chapter");
    return null;
  } finally {
    toast.dismiss(toastId);
  }
}

// =========================================================
// 6. REMOVE AN ENTIRE SUBJECT
// =========================================================
export async function removeSubject(classId, subjectName) {
  const toastId = toast.loading(`Deleting ${subjectName}...`);
  try {
    const response = await apiConnector(
      "DELETE",
      `${CLASS_BASE_API}/${classId}/subjects/${encodeURIComponent(subjectName)}`,
      null, // No body needed
      getAuthHeader()
    );

    toast.success(`${subjectName} deleted successfully!`);
    return response.data;
  } catch (error) {
    toast.error(`Failed to delete ${subjectName}`);
    return null;
  } finally {
    toast.dismiss(toastId);
  }
}

// =========================================================
// 7. DELETE ENTIRE CLASS
// =========================================================
export async function deleteClass(classId) {
  const toastId = toast.loading("Deleting class...");
  try {
    const response = await apiConnector(
      "DELETE",
      `${CLASS_BASE_API}/${classId}`,
      null,
      getAuthHeader()
    );

    toast.success("Class deleted successfully!");
    return response.data;
  } catch (error) {
    toast.error("Failed to delete class");
    return null;
  } finally {
    toast.dismiss(toastId);
  }
}